import React from "react";

import { Grid, GridSize } from "@mui/material";
import { format, parseISO } from "date-fns";
import _ from "lodash";
import { useTranslation } from "react-i18next";

import {
  Milestone,
  MilestoneType,
  MilestoneValue,
  MilestoneReferenceOrder,
  MilestoneReferenceShipment,
} from "../../../models/milestone";
import { MilestonesActualTranslateKey, MilestonesEstimatedTranslateKey } from "../../../util/enums";
import { formatInTimeZone } from "../../../util/formatInTimezone";
import { Input } from "../../Form";
import SkeletonComponent from "../Skeleton";
import useStyles, { Title } from "./styles";

interface MilestoneDatesProps<T> {
  prefixCustomTemplateViewMode?: React.ReactNode;
  suffixCustomTemplateViewMode?: React.ReactNode;

  prefixCustomTemplate?: React.ReactNode;
  suffixCustomTemplate?: React.ReactNode;
  type: MilestoneType;
  options: Milestone[];
  values: MilestoneValue[];
  onUpdate: (key: keyof T, value: MilestoneValue[] | null) => void;

  title?: string;
  viewOnly?: boolean;
  loading?: boolean;
  fieldSize?: GridSize;
  viewOnlyComponent?: (label: string, value?: string) => React.ReactNode;
}

interface MilestoneModel {
  milestones?: Milestone[];
}

export const renderMilestoneDateByTypeAndReference = (
  milestoneValues: MilestoneValue[],
  milestoneType: MilestoneType,
  milestoneReference: MilestoneReferenceShipment | MilestoneReferenceOrder
) => {
  if (!milestoneValues || !milestoneValues.length) return "";

  const milestoneOfValue = milestoneValues.find(
    (milestoneValue) =>
      milestoneValue.milestone &&
      milestoneValue.milestone.type === milestoneType &&
      milestoneValue.milestone.reference === milestoneReference
  );

  if (!milestoneOfValue || !milestoneOfValue.dateValue) return "";

  return format(new Date(milestoneOfValue.dateValue), "dd/MM/yyyy");
};

export const getDateValueInTimeZone = (value = "", format?: string, timezone?: string) => {
  const currentTimezone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  return formatInTimeZone(format ?? "yyyy-MM-dd", currentTimezone, value);
};

const getTranslateKeyFromMilestoneReferenceByType = (reference: string, type?: string) => {
  switch (type?.toLowerCase()) {
    case MilestoneType.ACTUAL.toLowerCase():
      return MilestonesActualTranslateKey[reference as keyof typeof MilestonesActualTranslateKey];
    case MilestoneType.ESTIMATED.toLowerCase():
      return MilestonesEstimatedTranslateKey[
        reference as keyof typeof MilestonesEstimatedTranslateKey
      ];
    default:
      return undefined;
  }
};

export const MilestoneDates = <T extends MilestoneModel>(props: MilestoneDatesProps<T>) => {
  const {
    options = [],
    values = [],
    viewOnly,
    onUpdate,
    loading,
    title,
    type,
    fieldSize,
    viewOnlyComponent,
  } = props;
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const onUpdateMilestone = (milestoneId?: number, dateValue?: string) => {
    if (!milestoneId) return;

    const valueIndex = values.findIndex(
      (milestoneValue) => Number(milestoneValue.milestoneId) === Number(milestoneId)
    );

    const formattedDate = dateValue
      ? formatInTimeZone("yyyy-MM-dd'T'HH:mm:ss.SSSxxx", "UTC", parseISO(dateValue))
      : undefined;

    const deepMilestoneValues = _.cloneDeep(values);

    const milestoneDateValue = {
      milestoneId,
      dateValue: formattedDate,
    };

    if (valueIndex > -1) {
      deepMilestoneValues.splice(valueIndex, 1, milestoneDateValue);
    } else {
      deepMilestoneValues.push(milestoneDateValue);
    }

    onUpdate("milestones", deepMilestoneValues);
  };

  const renderMilestoneDatesMap = () => {
    if (!options || options.length <= 0) {
      return (
        <Grid item xs={12} sm={6} md={3} lg={2}>
          <span className={classes.emptyMessage}>{translate("notHaveDatesYet")}</span>
        </Grid>
      );
    }

    const milestoneOptions = options.filter(
      (milestoneOption) => milestoneOption.type?.toLowerCase() === type.toLowerCase()
    );

    return milestoneOptions.map((milestoneOption) => {
      const milestoneValue = values.find(
        (milestoneValue) => Number(milestoneValue?.milestoneId) === Number(milestoneOption?.id)
      );
      const keyName = milestoneOption.reference || milestoneOption.title;
      const translateKey = getTranslateKeyFromMilestoneReferenceByType(
        milestoneOption.reference || "",
        milestoneOption.type
      );
      let label = milestoneOption.title;

      if (translateKey) label = translate(translateKey);

      return (
        <Grid item xs={12} sm={6} md={3} lg={fieldSize || 2} key={keyName}>
          {viewOnly && viewOnlyComponent ? (
            viewOnlyComponent?.(
              label,
              getDateValueInTimeZone(milestoneValue?.dateValue, "dd/MM/yyyy")
            )
          ) : (
            <Input
              type="date"
              name={keyName}
              label={label}
              value={getDateValueInTimeZone(milestoneValue?.dateValue)}
              disabled={viewOnly}
              onChange={(event) => onUpdateMilestone(milestoneOption.id, event.target.value)}
            />
          )}
        </Grid>
      );
    });
  };

  const renderMilestoneDates = () => {
    return (
      <Grid item container direction="column" rowGap={2}>
        <Grid item xs={12}>
          <Title>{title}</Title>
        </Grid>
        <SkeletonComponent isLoading={!!loading}>
          <Grid container direction="row" spacing={3}>
            {viewOnly
              ? props?.prefixCustomTemplateViewMode && (
                  <Grid item xs={12} sm={3}>
                    {props?.prefixCustomTemplateViewMode}
                  </Grid>
                )
              : props?.prefixCustomTemplate && (
                  <Grid item xs={12} sm={3}>
                    {props?.prefixCustomTemplate}
                  </Grid>
                )}

            {renderMilestoneDatesMap()}
            {viewOnly
              ? props?.suffixCustomTemplateViewMode && (
                  <Grid item xs={12} sm={3}>
                    {props?.suffixCustomTemplateViewMode}
                  </Grid>
                )
              : props?.suffixCustomTemplate && (
                  <Grid item xs={12} sm={3}>
                    {props?.suffixCustomTemplate}
                  </Grid>
                )}
          </Grid>
        </SkeletonComponent>
      </Grid>
    );
  };

  return renderMilestoneDates();
};
